import { FC } from 'react';
import _ from 'lodash';
import classNames from 'classnames';
import {
	Tooltip, isReddit, Avatar, AvatarEntity, getAvatarSource,
} from '@aleph/shared-components';
import { getAccessEntityName, RedditAccessEntity } from 'wb-reddit/app/routes/reports/utils';
import { getUserInitials } from 'wb-reddit/app/routes/users/utils';

const MARGIN = 2;
const OVERLAP_MARGIN = -14;
const X_MARGIN = 8;
const ICON_WIDTH = 36;

const calculateLayout = _.memoize((
	width: number,
	accessEntitiesLength: number,
	userGroupsCount: number,
) => {
	const usersCount = accessEntitiesLength - userGroupsCount;
	const overlapCoef = (ICON_WIDTH + MARGIN) / (ICON_WIDTH + OVERLAP_MARGIN);
	const availableSlots = Math.floor(((width - X_MARGIN) / (ICON_WIDTH + MARGIN)));
	let displayedAvatarsCount = 0;
	let areAvatarsTruncated = false;
	let shouldUsersOverlap = false;

	if (userGroupsCount + (usersCount / overlapCoef) > availableSlots) {
		// we have truncated avatars
		areAvatarsTruncated = true;
		shouldUsersOverlap = true;

		if (userGroupsCount > availableSlots - 1) {
			// all shown slots are userGroups
			displayedAvatarsCount = availableSlots - 1;
		} else {
			// mixed user groups and users
			displayedAvatarsCount = userGroupsCount
				+ Math.ceil((availableSlots - 1 - userGroupsCount) * overlapCoef);
		}
	} else {
		//	nothing is thruncated
		areAvatarsTruncated = false;

		displayedAvatarsCount = accessEntitiesLength;

		if (accessEntitiesLength > availableSlots) {
			shouldUsersOverlap = true;
		}
	}

	return {
		displayedAvatarsCount,
		areAvatarsTruncated,
		shouldUsersOverlap,
	};
}, (
	width,
	accessEntitiesLength,
	userGroupsCount,
) => `${width}-${accessEntitiesLength}-${userGroupsCount}`);

function getAvatarEntityType(entity: RedditAccessEntity) {
	if (entity?.__typename === 'UserGroup') {
		return AvatarEntity.Team;
	}

	if (entity?.__typename === 'AdvertiserAccount') {
		return AvatarEntity.Advertiser;
	}

	return AvatarEntity.User;
}

const RedditAccessImages: FC<{
	accessEntities: RedditAccessEntity[],
	width?: number,
}> = ({
	accessEntities,
	width = 168,
}) => {
	const userGroupsCount = isReddit() ? accessEntities?.filter(entity => entity?.__typename === 'UserGroup')?.length
		: accessEntities?.length;
	const layout = calculateLayout(width, accessEntities?.length, userGroupsCount);

	const shownAccessEntities = accessEntities?.slice?.(0, layout.displayedAvatarsCount);

	return (
		<div className="flex">
			{shownAccessEntities?.map((entity, idx) => (
				<Tooltip
					key={idx}
					placement="top"
					content={entity && getAccessEntityName(entity)}
					className="z-50"
				>
					<div
						className={classNames({
							'ml-[-14px]': layout.shouldUsersOverlap
									&& (idx > 0 || idx === layout.displayedAvatarsCount - 1)
									&& accessEntities[idx - 1]?.__typename !== 'UserGroup'
									&& entity?.__typename !== 'UserGroup',
							'mr-[2px]': !layout.shouldUsersOverlap
									|| entity?.__typename === 'UserGroup',
						})}
					>
						<Avatar
							src={getAvatarSource(entity)}
							entityType={getAvatarEntityType(entity)}
							entityUserInitials={entity?.__typename === 'User' ? getUserInitials(entity) : undefined}
						/>
					</div>
				</Tooltip>
			))
			}

			{layout.areAvatarsTruncated && (
				<Tooltip
					placement="top"
					content={accessEntities?.slice?.(layout.displayedAvatarsCount)?.map?.((entity, idx) => (
						<div key={idx}>
							{entity && getAccessEntityName(entity)}
						</div>
					))
					}
					className="z-50"
					showArrow
				>
					<div
						className={classNames(
							'min-w-[32px] w-[32px] h-[32px] cursor-default rounded-full text-[12px]',
							'font-bold border-[3px] border-solid border-white flex items-center',
							'justify-center text-white bg-gray-800 box-content',
							{
								'ml-[-14px]': layout.shouldUsersOverlap
										&& accessEntities[layout.displayedAvatarsCount - 1]?.__typename !== 'UserGroup',
							},
						)}
					>
						+{accessEntities.length - layout.displayedAvatarsCount ?? 0}
					</div>
				</Tooltip>
			)
			}
		</div>
	);
};

export default RedditAccessImages;
